import { useManagerRouting, useSetManagerRouting } from '../api/hooks';
import { ErrorBanner } from '../components/ui';

/**
 * A single, purpose-built policy toggle rather than a general rule builder.
 *
 * When on, every new lead that no more specific rule already claimed is
 * handed to a manager in rotation — evenly, and always to whoever currently
 * manages a team, with no list to keep in sync by hand. From there it is each
 * manager's own call: keep working the lead themselves, hand it to one
 * teammate, or distribute their queue evenly — both from the Leads page.
 */
export function LeadRoutingPage() {
  const { data, isPending, error } = useManagerRouting();
  const setRouting = useSetManagerRouting();

  return (
    <div className="page" style={{ maxWidth: 760 }}>
      <div className="page-head">
        <div>
          <h1>Lead routing</h1>
          <p className="sub">How brand-new leads find their first owner.</p>
        </div>
      </div>

      <ErrorBanner error={error ?? setRouting.error} />

      {isPending || !data ? (
        <div className="skeleton" style={{ height: 140, borderRadius: 14 }} />
      ) : (
        <>
          <div className="card">
            <div className="card-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <label className="switch" style={{ marginTop: 2 }}>
                <input
                  type="checkbox"
                  checked={data.enabled}
                  disabled={setRouting.isPending}
                  onChange={(e) => setRouting.mutate(e.target.checked)}
                />
                <span className="track" />
              </label>
              <div>
                <div style={{ fontWeight: 650, fontSize: 14.5 }}>
                  Round-robin new leads among managers
                </div>
                <p className="hint" style={{ marginTop: 4, maxWidth: '52ch' }}>
                  When on, a lead that no other assignment rule already claimed is handed to the
                  manager who has gone longest without one — evenly, automatically, the moment the
                  lead is created (manual entry or Meta). That manager then decides: keep it, assign
                  it to a teammate, or distribute their queue evenly from the Leads page.
                </p>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head">
              <h2>Managers in the rotation</h2>
              <div className="card-actions">
                <span className="chip chip-plain">{data.managers.length}</span>
              </div>
            </div>
            <div className="card-body" style={{ paddingTop: 6 }}>
              {data.managers.length === 0 ? (
                <p style={{ color: 'var(--muted)', margin: 0 }}>
                  No team currently has a manager assigned. Set a manager on a team before turning
                  this on, or new leads will have nowhere to route to.
                </p>
              ) : (
                <div className="tw">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Manager</th>
                        <th>Team</th>
                        <th>Auto-distribute</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.managers.map((m) => (
                        <tr key={m.id}>
                          <td><strong>{m.full_name}</strong></td>
                          <td>{m.team_name}</td>
                          <td>
                            {!m.auto_distribute ? (
                              <span className="chip chip-plain">Off — sits with the manager</span>
                            ) : m.auto_distribute_time ? (
                              <span className="chip" style={{ color: 'var(--good)', background: 'var(--good-bg)' }}>
                                Daily at {m.auto_distribute_time}
                              </span>
                            ) : (
                              <span className="chip" style={{ color: 'var(--good)', background: 'var(--good-bg)' }}>
                                Instant
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="hint" style={{ marginTop: 10 }}>
                Derived live from each team's manager — promote someone to manage a team and they
                join the rotation automatically, no list to update here. Each manager turns
                auto-distribute on or off for themselves from the Leads page.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
